import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { catchError, from, map, Observable, switchMap, throwError, timeout } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { PatchOrderStatus } from '../../model/order.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  private readonly REQUEST_TIMEOUT = 100000; // 30 seconds
  
  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  // Helper method to generate headers
  private getHeaders(): HttpHeaders {
    return this.authService.getHeaders();
  }

  // Fetch all orders
  getOrders(): Observable<any> {
    const headers = this.getHeaders();
    
    const endpoint = `${environment.api}/v1/orders`;

    return this.http.get<any>(endpoint, { headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError('Error fetching orders'))
    );
  }

  // order-service.ts
  getOrdersWithPaging(searchTerm: string, orderStatus: string[], pageNumber: number, pageSize: number): Observable<any> {
    const headers = this.getHeaders();
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize);

    if (searchTerm) {
      params = params.set('search', searchTerm);
    }

    if (orderStatus.length) {
      orderStatus.forEach(status => {
        params = params.append('OrderStatus', status);
      });
    }

    const endpoint = `${environment.api}/v1/orders`;
    return this.http.get<any>(endpoint, { headers, params }).pipe(
      catchError(this.handleError('Error fetching orders'))
    );
  }

  // Search orders with query parameters
  searchOrders(searchTerm: string, orderStatus: string[]): Observable<any> {
    const headers = this.getHeaders();
    
    let params = new HttpParams();
  
    if (searchTerm) {
      params = params.set('search', searchTerm);
    }
  
    if (orderStatus.length) {
      orderStatus.forEach(status => {
        params = params.append('OrderStatus', status); 
      });
    }
  
    const endpoint = `${environment.api}/v1/orders`;
    return this.http.get<any>(endpoint, { headers, params }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError('Error fetching orders'))
    );
  }

  // Edit an order
  editOrder(order: any): Observable<any> {
    const headers = this.getHeaders();
    return this.http.put<any>(`${environment.api}/${order.id}`, order, { headers });
  }

  // Duplicate an order
  duplicateOrder(orderId: number): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post<any>(`${environment.api}/${orderId}/duplicate`, {}, { headers });
  }

  // Change order status
  changeStatus(patchOrderStatus: PatchOrderStatus): Observable<any> {
    const headers = this.getHeaders();
    return this.http.patch<any>(`${environment.api}/v1/orders/order-change-status`, patchOrderStatus, { headers });
  }

  // Export orders
  exportOrders(searchTerm: string, orderStatus: string[]): Observable<{ filename: string, blob: Blob }> {
    const baseHeaders = this.getHeaders();
    const headers = baseHeaders.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

    let params = new HttpParams();
  
    if (searchTerm) {
      params = params.set('search', searchTerm);
    }
  
    if (orderStatus.length) {
      orderStatus.forEach(status => {
        params = params.append('OrderStatus', status); 
      });
    }

    return this.http.post(`${environment.api}/v1/orders/exports`, {}, {
      params: params,
      headers: headers,
      responseType: 'blob' as 'json',
      observe: 'response'
    }).pipe(
      switchMap(response => {
        return from(new Promise<{ filename: string, blob: Blob }>((resolve, reject) => {
          if (!response.body) {
            return reject(new Error('Response body is null'));
          }

          const reader = new FileReader();
          reader.onload = () => {
            try {
              const jsonResponse = JSON.parse(reader.result as string);
              if (!jsonResponse.data) {
                throw new Error('Invalid response structure');
              }

              const data = jsonResponse.data;
              const fileContents = data.fileContents;
              const contentType = data.contentType;
              const fileDownloadName = data.fileDownloadName || 'export.xlsx';

              const byteCharacters = atob(fileContents);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: contentType });

              resolve({ filename: fileDownloadName, blob: blob });
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsText(response.body as Blob);
        }));
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error exporting orders:', {
          message: error.message,
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          error: error.error
        });
        return throwError(() => new Error('Error exporting orders'));
      })
    );
  }

  // Batch delete orders
  deleteOrders(orderIds: number[]): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post<any>(`${environment.api}/batch-delete`, { ids: orderIds }, { headers });
  }

  // Batch export selected orders
  exportSelectedOrders(orderIds: number[]): Observable<Blob> {
    const headers = new HttpHeaders({
      ...this.getHeaders(),
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const params = new HttpParams().set('ids', orderIds.join(','));
    return this.http.get(`${environment.api}/export`, {
      params: params,
      headers: headers,
      responseType: 'blob'
    });
  }

  // Error handling helper
  private handleError(operation: string) {
    return (error: any): Observable<never> => {
      console.error(`${operation}:`, error);
      return throwError(() => new Error('Something went wrong; please try again later.'));
    };
  }
}